import type {
  AssetItem,
  MediaLibraryFolderTreeNode,
  MediaLibraryItem,
  OfficialAssetServerMaterial,
} from '$/api/server.types';
import type { WorkflowCanvasMediaItem } from '$/utils/openWorkflowWithMedia';
import {
  insertImage,
  insertVideo,
  insertDynamicImage,
} from '$/store/useCanvasObjectStore';
import { getImageElementByURL, getImageExtensionFromUrl, getUrlSearchParams } from '$/utils';
import { blobToFile, canvasToBlob, uploadFileBeforeEditor } from '$/utils/upload';
import { mtstatTrack } from '$/utils/mtstat';
import useFlowStore from '$/store/useFlowStore';
import useTeamFlowStore from '$/store/useTeamFlowStore';
import { resolveTeamTrackingContext } from '$/domains/workFlowV3/teamTracking';
import {
  DOCUMENT_CARD_ASPECT_RATIO,
  getAssetCardVariant,
  isDocumentCardLayout,
  resolveAssetFileExtension,
} from '$/utils/assetLibraryCardLayout';
import type {
  AssetModalFolderItem,
  AssetItemView,
  AssetGroupView,
  AssetListRowForGrouping,
  RoboneoAssetUploadClickLocation,
  MaterialOptionDef,
  AssetModalRawItem,
  AssetModalCommonProps,
  AssetModalInsertTarget,
} from './type';

/** 画布插入仅支持图片、视频（动图等仍归在图片流） */
const CANVAS_INSERT_MATERIAL_TYPES = new Set(['image', 'video']);

/** 工作流媒体节点插入支持图片、视频、音频 */
const WORKFLOW_INSERT_MATERIAL_TYPES = new Set(['image', 'video', 'audio']);

type OfficialAssetMergeImageKey =
  | 'main_image'
  | 'detail_image'
  | 'three_view_image'
  | 'four_view_image'
  | 'wearing_image';

interface OfficialAssetMergeRule {
  width: number;
  height: number;
  placements: Array<{
    key: OfficialAssetMergeImageKey;
    left: number;
    top: number;
    width: number;
    height: number;
  }>;
}

/** 官方素材各类型专属图片字段对应的合并版式。 */
const OFFICIAL_ASSET_MERGE_RULES: Record<'clothing' | 'accessory' | 'hairstyle', OfficialAssetMergeRule> = {
  clothing: {
    width: 2880,
    height: 4080,
    placements: [
      {
        key: 'main_image', left: 0, top: 0, width: 1440, height: 1920,
      },
      {
        key: 'detail_image', left: 1440, top: 0, width: 1440, height: 1920,
      },
      {
        key: 'three_view_image', left: 0, top: 1920, width: 2880, height: 2160,
      },
    ],
  },
  accessory: {
    width: 4500,
    height: 3000,
    placements: [
      {
        key: 'main_image', left: 0, top: 0, width: 2250, height: 3000,
      },
      {
        key: 'wearing_image', left: 2250, top: 0, width: 2250, height: 3000,
      },
    ],
  },
  hairstyle: {
    width: 4500,
    height: 3000,
    placements: [
      {
        key: 'main_image', left: 0, top: 0, width: 2250, height: 3000,
      },
      {
        key: 'four_view_image', left: 2250, top: 0, width: 2250, height: 3000,
      },
    ],
  },
};

/** 新素材库中文默认文件夹兜底名，服务端缺省 name 时使用。 */
const MEDIA_LIBRARY_SYSTEM_FOLDER_LABELS: Record<string, string> = {
  role: '角色',
  scene: '场景',
  prop: '道具',
  style: '风格',
  sfx: '音效',
  other: '其他',
};

/** 与 apps/roboneo-web SelectMaterialType（Figma 33766:107504）图标保持一致 */
export const MATERIAL_OPTION_DEFS: MaterialOptionDef[] = [
  { value: 'all', icon: 'squareGridCircleBold' },
  { value: 'image', icon: 'pictureBold' },
  { value: 'video', icon: 'videoV4Bold' },
  { value: 'audio', icon: 'musicV3Bold' },
  { value: 'document', icon: 'fileTextBold' },
  { value: 'excel', icon: 'tableBold' },
  { value: 'ppt', icon: 'chartBold' },
];

/** 未启用拖入画布时传给 useDrag 的占位数据，由 onDragStart 中 preventDefault 取消拖拽 */
export const DISABLED_ASSET_CANVAS_DRAG_PAYLOAD = {
  type: 'image' as const,
  url: '',
  uri: '',
  suffix: 'png',
  insertPos: 'point' as const,
  extraData: {} as Record<string, unknown>,
};

/** webpack/vite 静态资源导入可能是 string 或 { src }（与资产页 AssetManage 一致） */
export function staticImportedSrc(mod: string | { src: string; }): string {
  return typeof mod === 'string' ? mod : mod.src;
}

/**
 * 将文件夹列表排序：所有置顶项在前，并按 `pinned_at` 降序（最近置顶的更靠上），其余保持接口原有顺序。
 *
 * @param list 原始文件夹列表
 * @returns 排序后的新数组（不修改入参）
 */
export function sortFoldersForDropdown(list: AssetModalFolderItem[]): AssetModalFolderItem[] {
  const pinned = list.filter((f) => f.isPinned).sort((a, b) => b.pinnedAt - a.pinnedAt);
  const unpinned = list.filter((f) => !f.isPinned);
  return [...pinned, ...unpinned];
}

/**
 * 将素材库文件夹树拉平成弹窗可渲染列表，保留 depth 供左侧 Figma 文件夹 item 缩进。
 *
 * @param list 接口返回的文件夹森林
 * @param unnamedFolderText 服务端未返回文件夹名称时使用的已翻译兜底文案
 * @param generatedCharacterText 生成角色系统文件夹的已翻译文案
 * @returns 按树遍历顺序展开后的文件夹列表
 */
export function flattenMediaLibraryFolderTree(
  list: MediaLibraryFolderTreeNode[],
  unnamedFolderText: string,
  generatedCharacterText: string,
): AssetModalFolderItem[] {
  const out: AssetModalFolderItem[] = [];
  /** 深度优先遍历文件夹树，并为服务端缺省 depth 的节点补齐层级。 */
  const walk = (nodes: MediaLibraryFolderTreeNode[], fallbackDepth: number) => {
    nodes.forEach((node) => {
      const depth = Number.isFinite(node.depth) ? node.depth : fallbackDepth;
      const systemKey = String(node.system_key || '');
      const systemLabel = systemKey === 'gen_role'
        ? generatedCharacterText
        : MEDIA_LIBRARY_SYSTEM_FOLDER_LABELS[systemKey] || '';
      const assetCount = Number(node.asset_count ?? node.item_count ?? 0);
      out.push({
        id: String(node.id),
        name: node.name || systemLabel || unnamedFolderText,
        isPinned: false,
        pinnedAt: 0,
        depth,
        parentId: String(node.parent_id ?? 0),
        sortOrder: Number(node.sort_order ?? 0),
        hasChildren: Boolean(node.children?.length),
        folderKind: node.folder_kind,
        systemKey: node.system_key,
        assetCount: Number.isFinite(assetCount) && assetCount >= 0 ? assetCount : 0,
      });
      if (node.children?.length) {
        walk(node.children, depth + 1);
      }
    });
  };
  walk(list, 1);
  return out;
}

/**
 * 是否可将该素材类型插入当前 Fabric 画布。
 *
 * @param materialType 接口 `material_type`
 * @returns 是否支持
 */
export function isCanvasInsertableMaterialType(materialType: string): boolean {
  return CANVAS_INSERT_MATERIAL_TYPES.has(materialType);
}

/**
 * 是否可将该素材类型作为工作流媒体节点插入。
 *
 * @param materialType 接口 `material_type`
 * @returns 是否支持
 */
export function isWorkflowInsertableMaterialType(materialType: string): boolean {
  return WORKFLOW_INSERT_MATERIAL_TYPES.has(materialType);
}

/**
 * 内置资产库批量勾选时是否允许该类型（与对应场景的插入能力一致：画布仅图/视频，工作流为图/视频/音频，不含文档类）。
 *
 * @param insertTarget 插入目标场景
 * @param materialType 接口 `material_type`
 * @returns 是否可参与批量选择
 */
export function isBuiltInModalBatchSelectableMaterialType(
  insertTarget: AssetModalInsertTarget,
  materialType: string,
): boolean {
  if (insertTarget === 'workflow') {
    return isWorkflowInsertableMaterialType(materialType);
  }
  return isCanvasInsertableMaterialType(materialType);
}

/**
 * 资产卡悬停是否展示「插入」角标：画布仅图片/视频；工作流含音频；`agentTeam` 与画布一致。
 *
 * @param insertTarget 插入目标场景
 * @param materialType 素材类型
 * @returns 是否展示角标
 */
export function shouldShowAssetInsertHoverBadge(
  insertTarget: AssetModalInsertTarget,
  materialType: string,
): boolean {
  if (insertTarget === 'workflow') {
    return isWorkflowInsertableMaterialType(materialType);
  }
  return isCanvasInsertableMaterialType(materialType);
}

/**
 * 将接口时间戳格式化为「上次修订日期」展示
 */
export function formatAssetDate(ts: number): string {
  const d = new Date(ts * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

/** 安全解析素材库 JSON 字符串字段，解析失败时返回空对象。 */
function parseMediaLibraryJsonObject(value?: string): Record<string, any> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** 将未知数值转成有效正数，非法时返回 undefined。 */
function normalizeMediaLibraryPositiveNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** 从一组对象中按字段顺序读取第一个有效字符串，兼容新版素材库历史扩展字段。 */
function pickMediaLibraryStringFromRecords(records: Record<string, any>[], keys: string[]): string {
  for (const key of keys) {
    for (const record of records) {
      const value = record?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }
  return '';
}

/** 从保存时写入的 gen_params 中找到首个 media，供旧数据缺少顶层 url 时兜底预览。 */
function getMediaLibraryGenParamsFirstMedia(genParams: Record<string, any>): Record<string, any> {
  const candidates = [
    genParams.media_list,
    genParams.node_data?.media_list,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const media = candidate.find((item) => item && typeof item === 'object' && !Array.isArray(item));
      if (media) return media;
    }
  }
  return {};
}

/** 读取新版素材库保存时写入 ext 的后缀，文本/脚本无后缀时按 markdown 展示。 */
function resolveMediaLibraryFileExtension(item: MediaLibraryItem, ext: Record<string, any>): string {
  const explicit = String(ext.file_extension ?? ext.format ?? '').replace(/^\./, '').toLowerCase().trim();
  if (explicit) return explicit;
  const urlExt = resolveAssetFileExtension(item.url, item.material_type);
  if (urlExt) return urlExt;
  const materialType = String(item.material_type || '').toLowerCase();
  if (materialType === 'text' || materialType === 'script') return 'md';
  return '';
}

/** 将新版素材库素材类型映射到弹窗可显示/可回灌的类型。 */
function normalizeMediaLibraryMaterialType(materialType: string): string {
  const normalized = materialType.toLowerCase();
  if (normalized === 'text' || normalized === 'script') {
    return normalized;
  }
  return normalized || 'image';
}

/**
 * 将新版 `/media_library/item/list.json` 行适配为旧弹窗视图项，保留 mediaLibraryItemId 供 TeamFlow 回写。
 *
 * @param item 新版素材库列表项
 * @returns 弹窗列表用视图项
 */
export function mapMediaLibraryItemToModalViewItem(item: MediaLibraryItem): AssetItemView {
  const extJson = parseMediaLibraryJsonObject(item.ext);
  const genParams = parseMediaLibraryJsonObject(item.gen_params);
  const genParamsFirstMedia = getMediaLibraryGenParamsFirstMedia(genParams);
  const mediaRecords = [item as Record<string, any>, extJson, genParamsFirstMedia, genParams];
  const materialType = normalizeMediaLibraryMaterialType(String(item.material_type || ''));
  const mediaUrl = pickMediaLibraryStringFromRecords(mediaRecords, [
    'url',
    'media_url',
    'mediaUrl',
    'origin_url',
    'originUrl',
    'uri',
    'src',
  ]);
  const coverUrl = pickMediaLibraryStringFromRecords(mediaRecords, [
    'thumbnail_url',
    'thumbnailUrl',
    'cover_url',
    'coverUrl',
    'watermark_cover_url',
    'watermarkCoverUrl',
    'poster',
    'preview_url',
    'previewUrl',
  ]);
  const watermarkUrl = pickMediaLibraryStringFromRecords(mediaRecords, ['watermark_url', 'watermarkUrl']);
  const watermarkCoverUrl = pickMediaLibraryStringFromRecords(mediaRecords, ['watermark_cover_url', 'watermarkCoverUrl']);
  const compressUrl = pickMediaLibraryStringFromRecords(mediaRecords, [
    'compress_url',
    'compressUrl',
  ]);
  const watermarkCompressUrl = pickMediaLibraryStringFromRecords(mediaRecords, [
    'watermark_compress_url',
    'watermarkCompressUrl',
  ]);
  const ext = resolveMediaLibraryFileExtension({ ...item, url: mediaUrl }, extJson);
  const cardVariant = getAssetCardVariant(ext, materialType);
  const docLayout = isDocumentCardLayout(cardVariant, materialType);
  const isAudio = materialType === 'audio';
  const width = normalizeMediaLibraryPositiveNumber(extJson.width ?? genParams.width);
  const height = normalizeMediaLibraryPositiveNumber(extJson.height ?? genParams.height);
  const duration = normalizeMediaLibraryPositiveNumber(extJson.duration ?? genParams.duration);
  let listAspectRatio: number;
  if (docLayout) {
    listAspectRatio = DOCUMENT_CARD_ASPECT_RATIO;
  } else if (isAudio) {
    listAspectRatio = 1;
  } else {
    listAspectRatio = assetListItemAspectRatio(width, height);
  }

  const raw = {
    id: item.id,
    name: item.name,
    task_type: 'agent-team',
    tool_type: '',
    material_type: materialType,
    source_type: 'agent-team',
    source_id: String(extJson.source_id ?? ''),
    url: mediaUrl,
    thumbnail_url: coverUrl,
    watermark_url: watermarkUrl,
    watermark_cover_url: watermarkCoverUrl,
    compress_url: compressUrl,
    watermark_compress_url: watermarkCompressUrl,
    is_favorited: 0,
    created_at: item.created_at,
    file: { size: item.file_size, format: ext },
    image: materialType === 'image' ? {
      width, height, size: item.file_size, format: ext,
    } : undefined,
    video: materialType === 'video' ? {
      width, height, duration, size: item.file_size, format_name: ext,
    } : undefined,
    audio: materialType === 'audio' ? { duration, size: item.file_size, format_name: ext } : undefined,
    width,
    height,
    duration,
    size: item.file_size,
    file_name: item.name,
    file_size: item.file_size,
    gen_params: item.gen_params,
    ext: item.ext,
    mediaLibraryItemId: item.id,
    media_library_item_id: item.id,
  } as AssetModalRawItem;

  return {
    id: String(item.id),
    coverUrl: coverUrl || mediaUrl,
    mediaUrl,
    materialType,
    aspectRatio: listAspectRatio,
    fileExtension: ext,
    cardVariant,
    isWorkflow: false,
    isFavorite: false,
    raw,
  };
}

/**
 * 判断视图项是否满足弹窗素材类型筛选；新版文本/脚本归在“文件”筛选。
 *
 * @param item 视图项
 * @param filterValue 筛选值
 * @returns 是否保留
 */
export function isAssetModalMaterialFilterMatched(
  item: AssetItemView,
  filterValue: 'all' | 'image' | 'video' | 'audio' | 'document' | 'excel' | 'ppt',
): boolean {
  if (filterValue === 'all') return true;
  if (filterValue === 'document') {
    return ['document', 'text', 'script'].includes(item.materialType);
  }
  return item.materialType === filterValue;
}

/**
 * 房间视图分组键：优先 room_id，无则 source_id（与 AssetManage 一致）。
 */
export function assetRoomGroupKey(row: AssetListRowForGrouping): string {
  const byRoom = (row.room_id ?? '').trim();
  if (byRoom) return byRoom;
  const bySource = (row.source_id ?? '').trim();
  return bySource || '__ungrouped__';
}

/**
 * 按时间或房间分组资产列表，逻辑与 apps/roboneo-web AssetManage 的 buildAssetGroupsFromList 一致。
 *
 * @param listGroupMode 时间视图按创建日；房间视图按 room_id（同一 room_id 为同一房间），标题优先 room_name
 * @param list 接口原始行（需含 created_at、source_id，可选 room_id、room_name）
 * @param items 与 list 顺序一一对应的视图项
 * @param sortOrder 分组顺序与列表接口一致
 */
export function buildAssetModalGroupsFromList(
  listGroupMode: 'time' | 'room',
  list: AssetListRowForGrouping[],
  items: AssetItemView[],
  sortOrder: 'asc' | 'desc',
): AssetGroupView[] {
  if (listGroupMode === 'time') {
    const groupMap = new Map<string, AssetItemView[]>();
    const groupMaxTime = new Map<string, number>();
    list.forEach((a, i) => {
      const dateKey = formatAssetDate(a.created_at).slice(0, 10);
      if (!groupMap.has(dateKey)) {
        groupMap.set(dateKey, []);
        groupMaxTime.set(dateKey, a.created_at);
      } else {
        const prev = groupMaxTime.get(dateKey)!;
        if (a.created_at > prev) groupMaxTime.set(dateKey, a.created_at);
      }
      groupMap.get(dateKey)!.push(items[i]);
    });
    return Array.from(groupMap.entries())
      .sort(([a], [b]) => (sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b)))
      .map(([dateKey, groupItems]) => ({
        id: dateKey,
        name: dateKey,
        lastModified: formatAssetDate(groupMaxTime.get(dateKey) ?? 0),
        items: groupItems,
      }));
  }

  const groupMap = new Map<string, AssetItemView[]>();
  const groupMaxTime = new Map<string, number>();
  const groupRoomTitle = new Map<string, string>();
  list.forEach((a, i) => {
    const roomKey = assetRoomGroupKey(a);
    if (!groupMap.has(roomKey)) {
      groupMap.set(roomKey, []);
      groupMaxTime.set(roomKey, a.created_at);
    } else {
      const prev = groupMaxTime.get(roomKey)!;
      if (a.created_at > prev) groupMaxTime.set(roomKey, a.created_at);
    }
    groupMap.get(roomKey)!.push(items[i]);
    const title = (a.room_name ?? '').trim();
    if (title && !groupRoomTitle.has(roomKey)) {
      groupRoomTitle.set(roomKey, title);
    }
  });

  return Array.from(groupMap.entries())
    .sort((x, y) => (groupMaxTime.get(y[0]) ?? 0) - (groupMaxTime.get(x[0]) ?? 0))
    .map(([roomKey, groupItems]) => ({
      id: roomKey,
      name: roomKey === '__ungrouped__' ? '其他' : (groupRoomTitle.get(roomKey) || roomKey),
      lastModified: formatAssetDate(groupMaxTime.get(roomKey) ?? 0),
      items: groupItems,
    }));
}

/** 列表项宽高有效时返回 width/height，供 masonry 与 CSS aspect-ratio 使用；否则沿用原占位比例 */
export function assetListItemAspectRatio(width?: number, height?: number): number {
  const w = Number(width);
  const h = Number(height);
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return w / h;
  }
  return 183 / 100;
}

/**
 * 将资产列表接口单行映射为弹层卡片视图项（字段与 apps/roboneo-web AssetManage `mapServerRowToPageAssetItem` 对齐）。
 *
 * @param a 接口返回的资产行
 * @returns 弹层列表用视图项
 */
export function mapAssetRowToModalViewItem(a: AssetItem): AssetItemView {
  const rowMaterialType = a.material_type || 'image';
  // 剧本历史记录是一条包含多集的资产；列表预览只取第一集的第一个文件。
  // 原始行仍保留全部 episodes，进入工作台时再读取完整目录。
  const mediaUrl = rowMaterialType === 'script' && Array.isArray(a.episodes)
    ? a.episodes[0]?.urls?.[0] || ''
    : a.url || '';
  const ext = resolveAssetFileExtension(mediaUrl, rowMaterialType);
  const cardVariant = getAssetCardVariant(ext, rowMaterialType);
  const docLayout = isDocumentCardLayout(cardVariant, rowMaterialType);
  const isAudio = rowMaterialType === 'audio';
  let listAspectRatio: number;
  if (docLayout) {
    listAspectRatio = DOCUMENT_CARD_ASPECT_RATIO;
  } else if (isAudio) {
    listAspectRatio = 1;
  } else {
    listAspectRatio = assetListItemAspectRatio(a.width, a.height);
  }
  return {
    id: String(a.id),
    coverUrl: a.thumbnail_url || mediaUrl,
    mediaUrl,
    materialType: rowMaterialType,
    aspectRatio: listAspectRatio,
    fileExtension: ext,
    cardVariant,
    isWorkflow: a.task_type === 'workflow',
    isFavorite: a.is_favorited === 1,
    raw: a,
  };
}

export { trackRoboneoAssetUploadClick } from './tracking';

/**
 * 根据当前插入目标推断 room_id：
 * - canvas / agent_page → URL search param `room_id`
 * - workflow → `useFlowStore.roomId`
 * - agentTeam → `useTeamFlowStore.roomId`
 */
export function resolveRoomIdByInsertTarget(insertTarget: AssetModalInsertTarget): string {
  if (insertTarget === 'workflow') {
    return useFlowStore.getState().roomId || '';
  }
  if (insertTarget === 'agentTeam') {
    return useTeamFlowStore.getState().roomId || '';
  }
  return getUrlSearchParams().get('room_id') || '';
}

/**
 * 将 insertTarget 映射到数据平台约定的 location 枚举值
 */
export function insertTargetToTrackLocation(
  insertTarget: AssetModalInsertTarget,
): RoboneoAssetUploadClickLocation {
  if (insertTarget === 'workflow') return 'workflow_page';
  if (insertTarget === 'agentTeam') return 'agent_teams_page';
  if (insertTarget === 'home_page') return 'home_page';
  return 'agent_page';
}

/**
 * 资产库资源添加引用成功埋点：添加成功时上报，点击 1 次上报 1 次。
 *
 * @param items 成功插入的素材视图项列表
 * @param insertTarget 插入目标场景
 * @param location 显式的入口位置；不传时自动从 insertTarget 推断
 */
export function trackRoboneoAssetUploadSuccess(
  items: AssetItemView[],
  insertTarget: AssetModalInsertTarget,
  location?: RoboneoAssetUploadClickLocation,
): void {
  if (!items.length) return;
  const roomId = resolveRoomIdByInsertTarget(insertTarget);
  const context = resolveTeamTrackingContext({ roomId });
  const loc = location || insertTargetToTrackLocation(insertTarget);
  const contentUrls = items.map((i) => i.mediaUrl).join(',');
  const contentTypes = items.map((i) => i.materialType).join(',');
  const formats = items.map((i) => i.fileExtension || '').join(',');
  const assetIds = items.map((i) => i.id).join(',');
  mtstatTrack('roboneo_asset_upload_success', {
    project_id: context.project_id,
    oid: context.oid,
    room_id: context.room_id,
    media_url: contentUrls,
    content_url: contentUrls,
    content_type: contentTypes,
    format: formats,
    asset_id: assetIds,
    location: loc,
  });
}

/** 资产库埋点 node_type 映射（与 TeamFlow 节点类型口径一致）。 */
const ASSET_LIBRARY_NODE_TYPE_MAP: Record<string, string> = {
  image: 'IMAGE_NODE',
  video: 'VIDEO_NODE',
  audio: 'AUDIO_NODE',
  text: 'TEXT_NODE',
  script: 'SCRIPT_NODE',
};

/** 资产库埋点 content_type 映射（大写枚举）。 */
const ASSET_LIBRARY_CONTENT_TYPE_MAP: Record<string, string> = {
  image: 'IMAGE',
  video: 'VIDEO',
  audio: 'AUDIO',
  text: 'TEXT',
  script: 'SCRIPT',
};

/** insertTarget 兜底 home_page 等场景的 room_id 解析（统一回退到 URL room_id）。 */
function resolveAssetLibraryRoomId(insertTarget: AssetModalCommonProps['insertTarget']): string {
  if (insertTarget === 'workflow' || insertTarget === 'agentTeam') {
    return resolveRoomIdByInsertTarget(insertTarget);
  }
  return getUrlSearchParams().get('room_id') || '';
}

/** 解析素材库卡片 ext JSON，读取保存时写入的 task_id / source_from 等扩展字段。 */
function parseAssetItemExtRecord(raw: AssetModalRawItem): Record<string, any> {
  if (typeof raw.ext !== 'string' || !raw.ext) return {};
  try {
    const parsed = JSON.parse(raw.ext);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** 从一组对象按字段顺序读取首个非空字符串。 */
function pickAssetTrackString(records: Record<string, any>[], keys: string[]): string {
  for (const key of keys) {
    for (const record of records) {
      const value = record?.[key];
      if (value != null && String(value).trim()) return String(value).trim();
    }
  }
  return '';
}

/** 将素材来源兼容值归一为大禹文档约定的 source_from 枚举，缺失时按上传来源兜底。 */
function normalizeAssetLibraryTrackSource(source: string): string {
  const normalizedSource = source.trim().toLowerCase();
  if (!normalizedSource) return 'upload';
  if (['agent', 'agent-team', 'agent_team', 'agent_teams'].includes(normalizedSource)) return 'agent_teams';
  if (['upload', 'import', 'user_upload'].includes(normalizedSource)) return 'upload';
  if (['workflow', 'workflow_run'].includes(normalizedSource)) return 'workflow_run';
  if (['tool', 'canvas', 'sed_editing', 'seed_editing'].includes(normalizedSource)) return 'sed_editing';
  return normalizedSource;
}

/**
 * 汇总素材库卡片曝光 / 添加埋点公共参数：node 上下文取自卡片视图项与保存时写入的 ext。
 */
export function buildAssetLibraryCardTrackParams(
  item: AssetItemView,
  insertTarget: AssetModalCommonProps['insertTarget'],
) {
  const ext = parseAssetItemExtRecord(item.raw);
  const records = [item.raw as Record<string, any>, ext];
  const context = resolveTeamTrackingContext({
    roomId: resolveAssetLibraryRoomId(insertTarget),
  });
  return {
    project_id: context.project_id,
    oid: context.oid,
    room_id: context.room_id,
    node_id: item.id,
    node_type: ASSET_LIBRARY_NODE_TYPE_MAP[item.materialType] ?? '',
    content_type: ASSET_LIBRARY_CONTENT_TYPE_MAP[item.materialType] ?? '',
    media_url: item.mediaUrl,
    task_id: pickAssetTrackString(records, ['task_id', 'taskId', 'source_id', 'sourceId']),
    sub_task_id: pickAssetTrackString(records, ['sub_task_id', 'subTaskId']),
    duration: pickAssetTrackString(records, ['duration']),
    source_from: normalizeAssetLibraryTrackSource(
      pickAssetTrackString(records, [
        'content_source',
        'contentSource',
        'source_from',
        'sourceFrom',
        'task_type',
        'source_type',
      ]),
    ),
  };
}

/**
 * 历史记录优先保留 IP / 剧本 Agent 的明确来源，避免被通用任务类型覆盖；时间沿用列表原值。
 */
export function trackRoboneoHistoryMedia(event: 'show' | 'click', item: AssetItemView): void {
  const records = [item.raw as Record<string, any>, parseAssetItemExtRecord(item.raw)];
  const agentSource = records
    .flatMap((record) => [record.content_source, record.source_from, record.task_type, record.source_type])
    .find((value) => value === 'ip_agent' || value === 'script_agent');
  const source = agentSource || pickAssetTrackString(records, ['content_source', 'source_from', 'task_type', 'source_type']);
  mtstatTrack(`roboneo_history_media_${event}`, {
    media_type: item.materialType,
    media_url: item.mediaUrl,
    create_at: pickAssetTrackString(records, ['create_at', 'created_at']),
    upload_at: pickAssetTrackString(records, ['upload_at', 'uploaded_at']),
    source_from: source ? normalizeAssetLibraryTrackSource(source) : 'history_list',
  });
}

/** 资产库弹窗曝光埋点。 */
export function trackRoboneoAssetLibraryShow(insertTarget: AssetModalCommonProps['insertTarget']): void {
  const params = { room_id: resolveAssetLibraryRoomId(insertTarget) };
  mtstatTrack('roboneo_asset_library_show', params);
  mtstatTrack('roboneo_material_library_show', params);
}

/** 素材库卡片曝光埋点。 */
export function trackRoboneoAssetLibraryCardShow(
  item: AssetItemView,
  insertTarget: AssetModalCommonProps['insertTarget'],
): void {
  const params = buildAssetLibraryCardTrackParams(item, insertTarget);
  mtstatTrack('roboneo_asset_library_card_show', params);
}

/** 素材库个人资产卡片点击埋点，点击一次上报一次。 */
export function trackRoboneoAssetLibraryCardClick(
  item: AssetItemView,
  insertTarget: AssetModalCommonProps['insertTarget'],
): void {
  mtstatTrack('roboneo_asset_library_card_click', buildAssetLibraryCardTrackParams(item, insertTarget));
}

/**
 * 素材库添加到画布点击埋点（roboneo_asset_add_canvas_click），点击一次上报一次。
 */
export function trackRoboneoAssetAddCanvasClick(
  item: AssetItemView,
  insertTarget: AssetModalCommonProps['insertTarget'],
): void {
  mtstatTrack('roboneo_asset_add_canvas_click', buildAssetLibraryCardTrackParams(item, insertTarget));
}

/**
 * 将资产视图项转换为工作流媒体节点参数
 */
export function assetItemToWorkflowMediaItem(item: AssetItemView): WorkflowCanvasMediaItem | null {
  const { mediaUrl, materialType: itemMaterialType, coverUrl } = item;
  if (!isWorkflowInsertableMaterialType(itemMaterialType)) return null;
  if (!mediaUrl?.trim()) return null;
  const name = `asset-${item.id}`;
  const watermarkUrl = pickAssetTrackString([item.raw as Record<string, any>], ['watermark_url', 'watermarkUrl']);
  const watermarkCoverUrl = pickAssetTrackString([item.raw as Record<string, any>], ['watermark_cover_url', 'watermarkCoverUrl']);
  if (itemMaterialType === 'video') {
    return {
      kind: 'video',
      url: mediaUrl,
      coverUrl: coverUrl || item.coverUrl || mediaUrl,
      watermarkUrl: watermarkUrl || undefined,
      watermarkCoverUrl: watermarkCoverUrl || undefined,
      name,
    };
  }
  if (itemMaterialType === 'audio') {
    return { kind: 'audio', url: mediaUrl, name };
  }
  const ext = getImageExtensionFromUrl(mediaUrl).extension || 'png';
  return {
    kind: 'image',
    url: mediaUrl,
    coverUrl: item.coverUrl || mediaUrl,
    watermarkUrl: watermarkUrl || undefined,
    name,
    suffix: ext,
  };
}

/**
 * 按官方素材分类版式合并全部图片，并在保持原尺寸的前提下压缩上传。
 */
export async function mergeOfficialAssetImagesAndUpload(
  material: OfficialAssetServerMaterial,
): Promise<{ url: string; width: number; height: number; }> {
  let mergeRule: OfficialAssetMergeRule | undefined;
  if (material.detail_image && material.three_view_image) {
    mergeRule = OFFICIAL_ASSET_MERGE_RULES.clothing;
  } else if (material.wearing_image) {
    mergeRule = OFFICIAL_ASSET_MERGE_RULES.accessory;
  } else if (material.four_view_image) {
    mergeRule = OFFICIAL_ASSET_MERGE_RULES.hairstyle;
  }
  if (!mergeRule) {
    throw new Error(`Unsupported official asset merge material: ${material.id}`);
  }

  const sourceImages = await Promise.all(mergeRule.placements.map(({ key }) => {
    const sourceImage = material[key];
    if (!sourceImage) {
      throw new Error(`Missing official asset image: ${key}`);
    }
    return getImageElementByURL(sourceImage.url);
  }));

  const canvas = document.createElement('canvas');
  canvas.width = mergeRule.width;
  canvas.height = mergeRule.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to create official asset merge canvas');
  }

  mergeRule.placements.forEach((placement, index) => {
    context.drawImage(
      sourceImages[index],
      placement.left,
      placement.top,
      placement.width,
      placement.height,
    );
  });

  const mergedBlob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
  const [uploadedImage] = await uploadFileBeforeEditor([
    blobToFile(mergedBlob, `official-asset-${material.id}-merged.jpg`),
  ]);
  if (!uploadedImage?.url) {
    throw new Error('Failed to upload merged official asset image');
  }

  return {
    url: uploadedImage.url,
    width: mergeRule.width,
    height: mergeRule.height,
  };
}

/**
 * 将单条图片 / 视频 / 动图素材插入当前画布（不含音频校验与 Popover 关闭）。
 *
 * @param item 视图项，调用方需已排除音频并保证 `mediaUrl` 有效
 */
export async function insertAssetItemToCanvas(item: AssetItemView): Promise<void> {
  const { mediaUrl, materialType: itemMaterialType, coverUrl } = item;
  const watermarkUrl = pickAssetTrackString([item.raw as Record<string, any>], ['watermark_url', 'watermarkUrl']);
  const watermarkCoverUrl = pickAssetTrackString([item.raw as Record<string, any>], ['watermark_cover_url', 'watermarkCoverUrl']);
  if (itemMaterialType === 'video') {
    await insertVideo({
      url: mediaUrl,
      watermarkUrl: watermarkUrl || undefined,
      uri: '',
      name: `asset-${item.id}`,
      coverUrl: coverUrl || item.coverUrl || mediaUrl,
      watermarkCoverUrl: watermarkCoverUrl || undefined,
      insertPos: 'leftBottom',
      extraData: { role: 'user', source: 'asset_library', asset_id: item.raw.id },
    });
    mtstatTrack('canvas_import', {
      import_path: 6,
      import_type: 'video',
      video_num: 1,
      image_num: 0,
      url: mediaUrl,
    });
    return;
  }
  const ext = getImageExtensionFromUrl(mediaUrl).extension;
  const isDynamic = ext === 'gif' || ext === 'webp';
  if (isDynamic) {
    await insertDynamicImage({
      url: mediaUrl,
      watermarkUrl: watermarkUrl || undefined,
      uri: '',
      name: `asset-${item.id}`,
      coverUrl: item.coverUrl || mediaUrl,
      watermarkCoverUrl: watermarkCoverUrl || undefined,
      suffix: ext || 'gif',
      insertPos: 'leftBottom',
      extraData: { role: 'user', source: 'asset_library', asset_id: item.raw.id },
    });
  } else {
    await insertImage({
      url: mediaUrl,
      watermarkUrl: watermarkUrl || undefined,
      uri: '',
      name: `asset-${item.id}`,
      suffix: ext || 'png',
      insertPos: 'leftBottom',
      extraData: { role: 'user', source: 'asset_library', asset_id: item.raw.id },
    });
  }
  mtstatTrack('canvas_import', {
    import_path: 6,
    import_type: 'image',
    image_num: 1,
    video_num: 0,
    url: mediaUrl,
  });
}
